import React, { useEffect, useState } from "react";
import Footer from "../components/footer/Footer";
import ScrollToTopButton from "./topbutton/Top";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import SplashScreen from "./splashScreen/SplashScreen";
import { splashScreen } from "../portfolio";
import { StyleProvider } from "../contexts/StyleContext";
import { useLocalStorage } from "../hooks/useLocalStorage";
import Home from "./pages/home/Home";
import Imprint from "./pages/imprint/Imprint";
import "./Main.scss";

import "vanilla-cookieconsent/dist/cookieconsent.css";
import * as CookieConsent from "vanilla-cookieconsent";
import Privacy from "./pages/privacy/Privacy";

function updateGtagConsent() {
  if (CookieConsent.acceptedService(SERVICE_ANALYTICS_STORAGE, CAT_ANALYTICS)) {
    (function (w, d, s, l, i) {
      w[l] = w[l] || [];
      w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
      var f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l !== "dataLayer" ? "&l=" + l : "";
      j.async = true;
      j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
      f.parentNode.insertBefore(j, f);
    })(window, document, "script", "dataLayer", "GTM-NVPN3KPF");
  }

  gtag("consent", "update", {
    [SERVICE_ANALYTICS_STORAGE]: CookieConsent.acceptedService(
      SERVICE_ANALYTICS_STORAGE,
      CAT_ANALYTICS
    )
      ? "granted"
      : "denied",
    [SERVICE_AD_STORAGE]: CookieConsent.acceptedService(
      SERVICE_AD_STORAGE,
      CAT_ADVERTISEMENT
    )
      ? "granted"
      : "denied",
    [SERVICE_AD_USER_DATA]: CookieConsent.acceptedService(
      SERVICE_AD_USER_DATA,
      CAT_ADVERTISEMENT
    )
      ? "granted"
      : "denied",
    [SERVICE_AD_PERSONALIZATION]: CookieConsent.acceptedService(
      SERVICE_AD_PERSONALIZATION,
      CAT_ADVERTISEMENT
    )
      ? "granted"
      : "denied",
    [SERVICE_FUNCTIONALITY_STORAGE]: CookieConsent.acceptedService(
      SERVICE_FUNCTIONALITY_STORAGE,
      CAT_FUNCTIONALITY
    )
      ? "granted"
      : "denied",
    [SERVICE_PERSONALIZATION_STORAGE]: CookieConsent.acceptedService(
      SERVICE_PERSONALIZATION_STORAGE,
      CAT_FUNCTIONALITY
    )
      ? "granted"
      : "denied",
    [SERVICE_SECURITY_STORAGE]: CookieConsent.acceptedService(
      SERVICE_SECURITY_STORAGE,
      CAT_SECURITY
    )
      ? "granted"
      : "denied",
  });
}

const CAT_NECESSARY = "necessary";
const CAT_ANALYTICS = "analytics";
const CAT_ADVERTISEMENT = "advertisement";
const CAT_FUNCTIONALITY = "functionality";
const CAT_SECURITY = "security";

const SERVICE_AD_STORAGE = "ad_storage";
const SERVICE_AD_USER_DATA = "ad_user_data";
const SERVICE_AD_PERSONALIZATION = "ad_personalization";
const SERVICE_ANALYTICS_STORAGE = "analytics_storage";
const SERVICE_FUNCTIONALITY_STORAGE = "functionality_storage";
const SERVICE_PERSONALIZATION_STORAGE = "personalization_storage";
const SERVICE_SECURITY_STORAGE = "security_storage";

window.dataLayer = window.dataLayer || [];
// eslint-disable-next-line no-unused-vars
function gtag() {
  window.dataLayer.push(arguments);
}

gtag("consent", "default", {
  [SERVICE_AD_STORAGE]: "denied",
  [SERVICE_AD_USER_DATA]: "denied",
  [SERVICE_AD_PERSONALIZATION]: "denied",
  [SERVICE_ANALYTICS_STORAGE]: "denied",
  [SERVICE_FUNCTIONALITY_STORAGE]: "denied",
  [SERVICE_PERSONALIZATION_STORAGE]: "denied",
  [SERVICE_SECURITY_STORAGE]: "denied",
});
gtag("js", new Date());
gtag("config", "G-6HXVT18XVS");

const Main = () => {
  const darkPref = window.matchMedia("(prefers-color-scheme: dark)");
  const [isDark, setIsDark] = useLocalStorage("isDark", darkPref.matches);
  const [isShowingSplashAnimation, setIsShowingSplashAnimation] =
    useState(true);

  useEffect(() => {
    if (splashScreen.enabled) {
      const splashTimer = setTimeout(
        () => setIsShowingSplashAnimation(false),
        splashScreen.duration
      );
      return () => {
        clearTimeout(splashTimer);
      };
    }
    CookieConsent.run({
      onFirstConsent: () => {
        updateGtagConsent();
      },
      onConsent: () => {
        updateGtagConsent();
      },
      onChange: () => {
        updateGtagConsent();
      },

      categories: {
        [CAT_NECESSARY]: {
          enabled: true, // this category is enabled by default
          readOnly: true, // this category cannot be disabled
        },
        [CAT_ANALYTICS]: {
          autoClear: {
            cookies: [
              {
                name: /^_ga/, // regex: match all cookies starting with '_ga'
              },
              {
                name: "_gid", // string: exact cookie name
              },
            ],
          },
          // See: https://cookieconsent.orestbida.com/reference/configuration-reference.html#category-services
          services: {
            [SERVICE_ANALYTICS_STORAGE]: {
              label:
                "Enables storage (such as cookies) related to analytics e.g. visit duration.",
            },
          },
        },
        [CAT_ADVERTISEMENT]: {
          services: {
            [SERVICE_AD_STORAGE]: {
              label:
                "Enables storage (such as cookies) related to advertising.",
            },
            [SERVICE_AD_USER_DATA]: {
              label:
                "Sets consent for sending user data related to advertising to Google.",
            },
            [SERVICE_AD_PERSONALIZATION]: {
              label: "Sets consent for personalized advertising.",
            },
          },
        },
        [CAT_FUNCTIONALITY]: {
          services: {
            [SERVICE_FUNCTIONALITY_STORAGE]: {
              label:
                "Enables storage that supports the functionality of the website or app e.g. language settings.",
            },
            [SERVICE_PERSONALIZATION_STORAGE]: {
              label:
                "Enables storage related to personalization e.g. video recommendations.",
            },
          },
        },
        [CAT_SECURITY]: {
          services: {
            [SERVICE_SECURITY_STORAGE]: {
              label:
                "Enables storage related to security such as authentication functionality, fraud prevention, and other user protection.",
            },
          },
        },
      },
      guiOptions: {
        consentModal: {
          layout: "bar",
          equalWeightButtons: true,
        },
        preferencesModal: {
          layout: "box",
          flipButtons: false,
          equalWeightButtons: true,
        },
      },
      language: {
        default: "en",
        translations: {
          en: {
            consentModal: {
              title: "I Love Cookies 🍪",
              description:
                "I’ve baked some cookies for you—trust me, they’re essential for enjoying this website. It simply won’t work without them. To gather information and improve your experience, I’d also like to use some third-party cookies. Would that be okay?",
              acceptAllBtn: "Yeah that’s fine",
              acceptNecessaryBtn: "Nah I’m good",
              showPreferencesBtn: "Manage Individual preferences",
              equalWeightButtons: true,
              footer:
                '<a href="/privacy">Privacy Policy</a> <a href="/imprint">Imprint</a>',
            },
            preferencesModal: {
              title: "Manage Cookies",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              savePreferencesBtn: "Accept current selection",
              closeIconLabel: "Close modal",
              sections: [
                {
                  title: "Simon Loves Cookies!",
                  description:
                    "I use cookies to personalize the website. To give you the best experience, I remember your preferences and repeat visits. By clicking “Accept”, you consent to the use of all the cookies.",
                },
                {
                  title: "Neccessary Cookies",
                  description:
                    "These cookies are necessary for the website to function and cannot be switched off in our systems. They are usually only set in response to actions made by you which amount to a request for services, such as setting your privacy preferences, logging in or filling in forms.",

                  //this field will generate a toggle linked to the 'necessary' category
                  linkedCategory: CAT_NECESSARY,
                },
                {
                  title: "Performance and Analytics",
                  description:
                    "These cookies collect information about how you use our website. All of the data is anonymized and cannot be used to identify you.",
                  linkedCategory: CAT_ANALYTICS,
                  cookieTable: {
                    headers: {
                      name: "Name",
                      domain: "Service",
                      description: "Description",
                      expiration: "Expiration",
                    },
                    body: [
                      {
                        name: "_ga",
                        domain: "Google Analytics",
                        description:
                          'Cookie set by <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                        expiration: "Expires after 12 days",
                      },
                      {
                        name: "_gid",
                        domain: "Google Analytics",
                        description:
                          'Cookie set by <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                        expiration: "Session",
                      },
                    ],
                  },
                },
                {
                  title: "Advertising",
                  description:
                    'Google uses cookies for advertising, including serving and rendering ads, personalizing ads (depending on your ad settings at <a href="https://g.co/adsettings">g.co/adsettings</a>), limiting the number of times an ad is shown to a user, muting ads you have chosen to stop seeing, and measuring the effectiveness of ads.',
                  linkedCategory: CAT_ADVERTISEMENT,
                },
                {
                  title: "Functionality",
                  description:
                    "Cookies used for functionality allow users to interact with a service or site to access features that are fundamental to that service. Things considered fundamental to the service include preferences like the user’s choice of language, product optimizations that help maintain and improve a service, and maintaining information relating to a user’s session, such as the content of a shopping cart.",
                  linkedCategory: CAT_FUNCTIONALITY,
                },
                {
                  title: "Security",
                  description:
                    "Cookies used for security authenticate users, prevent fraud, and protect users as they interact with a service.",
                  linkedCategory: CAT_SECURITY,
                },
                {
                  title: "More information",
                  description:
                    'For any queries in relation to my policy on cookies and your choices, please <a href="#contact">contact me</a>',
                },
              ],
            },
          },
        },
      },
    });
  }, []);

  const changeTheme = () => {
    setIsDark(!isDark);
  };

  return (
    <div className={isDark ? "dark-mode" : null}>
      <StyleProvider value={{ isDark: isDark, changeTheme: changeTheme }}>
        {isShowingSplashAnimation && splashScreen.enabled ? (
          <SplashScreen />
        ) : (
          <>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<Home />}></Route>
                <Route path="/imprint" element={<Imprint />}></Route>
                <Route path="/privacy" element={<Privacy />}></Route>
              </Routes>
            </BrowserRouter>
            <Footer />
            <ScrollToTopButton />
          </>
        )}
      </StyleProvider>

      <noscript>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-NVPN3KPF"
          data-category={CAT_ANALYTICS}
          title="Google Tag Manager"
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
        ></iframe>
      </noscript>
      <script
        async
        defer
        src="https://www.googletagmanager.com/gtag/js?id=G-6HXVT18XVS"
        data-category={CAT_ANALYTICS}
      ></script>
    </div>
  );
};

export default Main;
